import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { businessAPI } from '../../api/businesses';

const BusinessSetup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const [formData, setFormData] = useState({
    business_name: '',
    business_type: 'salon',
    email: '',
    phone: '',
    address: '',
    currency_code: 'USD',
    latitude: '',
    longitude: '',
    timezone: browserTimeZone
  });

  const currencyOptions = [
    { value: 'PKR', label: 'Pakistan Rupees', symbol: '₨' },
    { value: 'USD', label: 'US Dollar', symbol: '$' },
    { value: 'EUR', label: 'Euro', symbol: '€' },
    { value: 'CHF', label: 'Swiss Franc', symbol: 'CHF' },
    { value: 'GBP', label: 'Pound', symbol: '£' }
  ];

  const useMyLocation = async () => {
    if (!navigator?.geolocation) {
      window.alert('Geolocation is not supported in this browser');
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos?.coords?.latitude;
        const lng = pos?.coords?.longitude;
        setFormData((prev) => ({
          ...prev,
          latitude: Number.isFinite(lat) ? String(lat) : '',
          longitude: Number.isFinite(lng) ? String(lng) : ''
        }));
        setGeoLoading(false);
      },
      (err) => {
        window.alert(err?.message || 'Failed to get location');
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const hasLat = String(formData.latitude || '').trim() !== '';
      const hasLng = String(formData.longitude || '').trim() !== '';
      if (hasLat !== hasLng) {
        window.alert('Please provide both latitude and longitude (or leave both empty)');
        setLoading(false);
        return;
      }

      const payload = {
        ...formData,
        latitude: hasLat ? Number(formData.latitude) : undefined,
        longitude: hasLng ? Number(formData.longitude) : undefined
      };
      await businessAPI.create(payload);
      navigate('/business/hours');
    } catch (error) {
      console.error('Failed to create business:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6">Set Up Your Business</h1>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Business Name *</label>
            <input
              type="text"
              required
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              className="w-full input"
              placeholder="Your Business Name"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Business Type *</label>
            <select
              required
              value={formData.business_type}
              onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
              className="w-full input"
            >
              <option value="salon">Salon / Spa</option>
              <option value="clinic">Clinic / Medical</option>
              <option value="gym">Gym / Fitness</option>
              <option value="ride">Ride / Transportation</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full input"
              placeholder="contact@yourbusiness.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full input"
              placeholder="+1 234 567 8900"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows="3"
              className="w-full input"
              placeholder="Your business address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Currency</label>
            <select
              value={formData.currency_code}
              onChange={(e) => setFormData({ ...formData, currency_code: e.target.value })}
              className="w-full input"
            >
              {currencyOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label} ({c.symbol})
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between gap-3">
              <label className="block text-sm font-medium mb-1">Location (Optional)</label>
              <button
                type="button"
                onClick={useMyLocation}
                disabled={geoLoading}
                className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-60"
              >
                {geoLoading ? 'Getting location...' : 'Use my location'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Latitude</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.0000001"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  className="w-full input"
                  placeholder="e.g. 24.8607"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Longitude</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.0000001"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  className="w-full input"
                  placeholder="e.g. 67.0011"
                />
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Timezone</label>
            <input
              type="text"
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              className="w-full input"
              placeholder="e.g. Asia/Karachi, Asia/Kolkata, UTC"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full btn-primary disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Continue to Working Hours'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BusinessSetup;
